import { packageVersion as webglApiVersion } from "@novorender/api";
import type { Webviewer } from "lib";

import type { RootState } from "app/store";
import {
    selectDefaultVisibility,
    selectDeviceProfile,
    selectMainObject,
    selectSelectionBasketMode,
} from "features/render";
import {
    selectBackendProvider,
    selectInitHomeViewpointId,
    selectInitViewpoint,
    selectProcoreContext,
    selectSceneId,
} from "slices/explorer";
import { collectMemoryStats } from "utils/memoryDiagnostics";

/** Selected ids beyond this are counted but not listed. */
const MAX_LISTED_IDS = 100;

/**
 * Everything a bug report usually needs, as one JSON-serialisable object.
 * @param params.visibility Id counts from the hidden/basket/highlighted contexts, which live outside Redux.
 */
export async function buildDebugReport({
    state,
    webviewer,
    visibility,
}: {
    state: RootState;
    webviewer: Webviewer;
    visibility: { highlightedIds: ArrayLike<number>; hiddenCount: number; basketCount: number };
}) {
    const procoreContext = selectProcoreContext(state);
    const highlightedIds = Array.from(visibility.highlightedIds);
    const camera = webviewer.camera.getCamera();

    return {
        timestamp: new Date().toISOString(),
        page: `${location.origin}${location.pathname}`,
        versions: {
            app: __APP_VERSION__,
            lib: `${__LIB_NAME__}@${__LIB_VERSION__}`,
            webglApi: webglApiVersion,
        },
        environment: {
            userAgent: navigator.userAgent,
            gpu: getGpuRenderer(),
            devicePixelRatio: window.devicePixelRatio,
            viewport: { width: window.innerWidth, height: window.innerHeight },
            deviceProfile: selectDeviceProfile(state),
        },
        scene: {
            backendProvider: selectBackendProvider(state),
            sceneId: selectSceneId(state),
            companyId: procoreContext?.companyId,
            projectId: procoreContext?.projectId,
            sceneVersionId: procoreContext?.sceneVersionId,
            sceneSliceId: procoreContext?.sceneSliceId,
            initViewpointId: selectInitViewpoint(state)?.id,
            homeViewpointId: selectInitHomeViewpointId(state),
        },
        selection: {
            mainObject: selectMainObject(state),
            count: highlightedIds.length,
            internalIds: highlightedIds.slice(0, MAX_LISTED_IDS),
        },
        visibility: {
            defaultVisibility: selectDefaultVisibility(state),
            selectionBasketMode: selectSelectionBasketMode(state),
            hiddenCount: visibility.hiddenCount,
            basketCount: visibility.basketCount,
        },
        camera: {
            kind: camera.kind,
            position: Array.from(camera.position),
            rotation: Array.from(camera.rotation),
            fov: camera.fov,
        },
        viewpoint: await webviewer.model.getViewpoint({ createSnapshot: false, collapseVisibilityExceptions: true }),
        // Sync providers only: a full `collectMemoryDiagnostics` heap reading can take seconds.
        memory: {
            jsHeapBytes: (performance as Performance & { memory?: { usedJSHeapSize?: number } }).memory?.usedJSHeapSize,
            ...collectMemoryStats(),
        },
    };
}

function getGpuRenderer(): string | undefined {
    const gl = document.createElement("canvas").getContext("webgl");
    const info = gl?.getExtension("WEBGL_debug_renderer_info");
    const renderer = info ? (gl?.getParameter(info.UNMASKED_RENDERER_WEBGL) as string) : undefined;
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
    return renderer;
}
